import {
  Atividade,
  ProblemaAtividade,
} from "@/repositorios/atividades/atividades.modelos"
import {
  EventoASerIniciado,
  EventoEmAndamento,
  ModeloEventoCalendario,
  ModeloEventoCalendarioEmAndamento,
  ModeloEventoCalendarioPodeSerIniciada,
  StatusEventoCalendario,
  StatusEventoPlanejado,
} from ".."
import { usarDataHora } from "@/composable/UsarDataHora/usarDataHora"
import { AtividadePlanejada } from "@/repositorios/eventos/eventos.modelo"
import { usarEventosPlanejadosStore } from "../EventosPlanejados/EventosPlanejados.store"
import { LeitorEstados, Paginador } from "@/composable/UsarLeitorDeDados/usarLeitorDeDados"

const {
  converterParaTimeZoneLocal,
  adicionarMinutosAUmaData,
  verificarSeDatasSaoIguaisIgnorandoHoras,
  hoje,
} = usarDataHora()

type estadosDasRequisicoesDoCalendario = {
  statusAtividades: LeitorEstados["status"]
  statusEventosPlanejados: LeitorEstados["status"]
}

export const tratarErrosDeRequisicao = ({
  statusAtividades,
  statusEventosPlanejados,
}: estadosDasRequisicoesDoCalendario): "erro" | "sucesso" => {
  const todosOsEstados = [
    statusAtividades,
    statusEventosPlanejados,
  ]
  const existeEstadoDeErro = todosOsEstados.some((estado) => estado === "erro")

  if (existeEstadoDeErro) {
    return "erro"
  }

  return "sucesso"
}


export const definirCacheNosLeitores = (leitorDeAtividades: Paginador<any>, utilizaCache: boolean): void => {
  leitorDeAtividades.sobrescreverCabecalhos({
    "X-Timesheet-Cache-Ignore": !utilizaCache,
  })
}

export const definirStatus = (
  problemas?: ProblemaAtividade[],
  travado?: boolean,
  status?: StatusEventoCalendario
): StatusEventoCalendario => {
  const temProblemas = problemas && problemas.length > 0
  if (travado) {
    return "travado"
  }
  if (temProblemas) {
    const temAlgumaJustificativa = problemas.some((pr) => pr.justificativa)

    if (temAlgumaJustificativa) {
      return "justificado"
    }
    if (!temAlgumaJustificativa) {
      return "nao-justificado"
    }
  }

  if (status) {
    return status
  }

  return "normal"
}

// mapeadores de eventos e atividades

export const mapearAtividadesParaModeloEventoCalendario = (
  atividade: Atividade
): ModeloEventoCalendario => {
  const inicio = converterParaTimeZoneLocal(
    atividade.dataInicio,
    atividade.horaInicio
  )
  const fim = converterParaTimeZoneLocal(atividade.dataFim, atividade.horaFim)
  const marcacaoInicio = atividade.marcacoes?.find(
    (marcacao) => marcacao.tipo === "inicio"
  )
  const marcacaoFim = atividade.marcacoes?.find(
    (marcacao) => marcacao.tipo === "fim"
  )

  return {
    id: atividade.id,
    idUsuarioCriador: atividade.idUsuarioCriador,
    start: inicio,
    end: fim,
    duracao: atividade.duracaoEmMinutos,
    profissionais: [atividade.profissionalQueRealizou],
    status: definirStatus(atividade.problemasJustificativas, atividade.travado),
    tipoDeAtividade: atividade.tipoDeAtividade,
    ...(atividade.listaDeAtributosCaracteristicaComValor && {
      listaDeAtributosCaracteristicaComValor: atividade.listaDeAtributosCaracteristicaComValor
    }),
    modoDeRegistroInicio:
      marcacaoInicio?.modoDeRegistro ?? atividade.modoDeRegistroInicio ?? null,
    modoDeRegistroFim:
      marcacaoFim?.modoDeRegistro ?? atividade.modoDeRegistroFim ?? null,
    problemas: atividade.problemasJustificativas?.length
      ? atividade.problemasJustificativas
      : null,
    projeto: atividade.projeto,
    faseDaAtividade: "naoPodeIniciar",
  }
}

export const mapearAtividadesParaModeloEventoCalendarioPodeSerIniciada = (
  atividade: EventoASerIniciado
): ModeloEventoCalendarioPodeSerIniciada => {
  const inicio = converterParaTimeZoneLocal(
    atividade.dataInicio,
    atividade.horaInicio
  )
  const fim = converterParaTimeZoneLocal(atividade.dataFim, atividade.horaFim)

  return {
    id: atividade.id,
    start: inicio,
    end: fim,
    duracao: atividade.duracaoEmMinutos,
    profissionais: atividade.profissionalQueRealizou,
    status: definirStatus(
      atividade.problemasJustificativas,
      atividade.travado,
      atividade.status
    ),
    atividade: atividade.atividade,
    listaDeAtributosCaracteristicaComValor: atividade.listaDeAtributosCaracteristicaComValor,
    tipoDeAtividade: atividade.tipoDeAtividade,
    modoDeRegistroInicio: atividade.modoDeRegistroInicio,
    modoDeRegistroFim: atividade.modoDeRegistroFim,
    problemas: atividade.problemasJustificativas?.length
      ? atividade.problemasJustificativas
      : null,
    projeto: atividade.projeto,
    faseDaAtividade: atividade.faseDaAtividade,
    calendario: atividade.calendario,
  }
}

export const mapearAtividadesParaModeloEventoCalendarioEmAndamento = (
  atividade: EventoEmAndamento
): ModeloEventoCalendarioEmAndamento => {
  const inicio = converterParaTimeZoneLocal(
    atividade.dataInicio,
    atividade.horaInicio
  )
  const fim = adicionarMinutosAUmaData(
    inicio,
    atividade.duracaoPadraoEmMinutos!
  )

  return {
    id: atividade.id,
    idUsuarioCriador: atividade.idUsuarioCriador,
    start: inicio,
    end: fim,
    inicioPlanejado: atividade.horaInicioPlanejado ?
      converterParaTimeZoneLocal(
        atividade.dataInicio,
        atividade.horaInicioPlanejado
      ) : null,
    fimPlanejado: atividade.horaFimPlanejado ?
      converterParaTimeZoneLocal(atividade.dataFim, atividade.horaFimPlanejado) : null,
    duracao: atividade.duracaoEmMinutos ?? atividade.duracaoPadraoEmMinutos,
    profissionais: atividade.profissionalQueRealizou,
    status: definirStatus(
      atividade.problemasJustificativas,
      atividade.travado,
      atividade.status
    ),
    tipoDeAtividade: atividade.tipoDeAtividade,
    listaDeAtributosCaracteristicaComValor: atividade.listaDeAtributosCaracteristicaComValor,
    modoDeRegistroInicio: atividade.modoDeRegistroInicio,
    modoDeRegistroFim: atividade.modoDeRegistroFim,
    problemas: atividade.problemasJustificativas?.length
      ? atividade.problemasJustificativas
      : null,
    projeto: atividade.projeto,
    faseDaAtividade: atividade.faseDaAtividade,
  }
}

// auxiliares utilizadas nos filtros

export const mapearAtividadesParaHoje = (
  atividadesParaHoje: Atividade[],
  eventos: AtividadePlanejada[]
) => {

  const definirStatusDaAtividade = (idEvento?: string | null): StatusEventoPlanejado => {
    if (idEvento) {
      return "planejado"
    }

    return "naoPlanejado"
  }

  const atividadesEmAndamento: EventoEmAndamento[] = atividadesParaHoje.map(
    (atividade: Atividade) => {
      const eventoDaAtividade: AtividadePlanejada[] = eventos.filter(
        (evento) => evento.id === atividade.idAtividadePlanejada
      )

      if (eventoDaAtividade[0]) {
        return {
          ...atividade,
          profissionalQueRealizou: [atividade.profissionalQueRealizou],
          duracaoPadraoEmMinutos: eventoDaAtividade[0].duracaoEmMinutos,
          horaInicioPlanejado: eventoDaAtividade[0].horaInicio,
          horaFimPlanejado: eventoDaAtividade[0].horaFim,
          faseDaAtividade: "emAndamento",
          status: definirStatusDaAtividade(atividade.idAtividadePlanejada),
          listaDeAtributosCaracteristicaComValor: atividade.listaDeAtributosCaracteristicaComValor ?? []
        }
      }

      return {
        ...atividade,
        status: definirStatusDaAtividade(atividade.idAtividadePlanejada),
        profissionalQueRealizou: [atividade.profissionalQueRealizou],
        faseDaAtividade: "emAndamento",
        listaDeAtributosCaracteristicaComValor: atividade.listaDeAtributosCaracteristicaComValor ?? []
      }
    }
  )

  return atividadesEmAndamento
}

const mapearEventosNoFormatoDeAtividades = (
  evento: AtividadePlanejada
): EventoASerIniciado => {

  return {
    id: evento.id,
    ativo: evento.ativo,
    travado: evento.travado,
    deletado: evento.deletado,
    dataInicio: evento.dataInicio,
    dataFim: evento.dataFim,
    horaInicio: evento.horaInicio,
    horaFim: evento.horaFim,
    duracaoEmMinutos: evento.duracaoEmMinutos,
    status: "planejado",
    profissionalQueRealizou: evento.profissionaisAlocados,
    atividade: {
      codigo: evento.atividadePlanejada.codigo,
      id: evento.atividadePlanejada.id,
    },
    tipoDeAtividade: evento.atividadePlanejada.tipoDeAtividade,
    faseDaAtividade: "podeSerIniciada",
    projeto: evento.atividadePlanejada.projeto,
    modoDeRegistroInicio: null,
    modoDeRegistroFim: null,
    problemasJustificativas: [],
    listaDeAtributosCaracteristicaComValor: evento.listaDeAtributosCaracteristicaComValor,
    calendario: evento.calendarioQueCriouId,
  }
}

const filtrarEventosQueNaoIniciamHoje = (evento: AtividadePlanejada) => {
  const inicioDoEvento = converterParaTimeZoneLocal(
    evento.dataInicio,
    evento.horaInicio
  )
  return !verificarSeDatasSaoIguaisIgnorandoHoras(inicioDoEvento, hoje)
}

const filtrarEventosQueIniciamHoje = (evento: AtividadePlanejada) => {
  const dataInicioDoEvento = converterParaTimeZoneLocal(
    evento.dataInicio,
    evento.horaInicio
  )
  return verificarSeDatasSaoIguaisIgnorandoHoras(dataInicioDoEvento, hoje)
}

// filtros de eventos e atividades

/**
 * com base nas atividades e eventos planejados, retorna as atividades que podem ser iniciadas.
 * O campo faseDaAtividade: "podeSerIniciada" indica que a atividade pode ser iniciada.
 * Retorna um array de objetos ModeloEventoCalendarioPodeSerIniciada
 * @param atividades
 * @param eventos
 * @returns
 */
export const filtrarEMapearAtividadesQuePodemSerIniciadas = (
  atividades: Atividade[],
  eventos: AtividadePlanejada[]
): ModeloEventoCalendarioPodeSerIniciada[] => {
  const filtrandoAtividadesParaHoje = atividades.filter(
    (atividade) => atividade.dataFim === null
  )

  const atividadesParaHojeFiltradas = mapearAtividadesParaHoje(
    filtrandoAtividadesParaHoje,
    eventos
  )

  const idsDeEventosDeAtividadesRealizadas = atividades
    .filter(
      (atividade: Atividade) => atividade.idAtividadePlanejada !== null
    )
    .map((atividade: Atividade) => atividade.idAtividadePlanejada)

  const arrayDeIdsDeEventosDeAtividadesPlanejadas = Object.values(
    idsDeEventosDeAtividadesRealizadas
  )

  let eventosPlanejadosParaHoje = eventos.filter(filtrarEventosQueIniciamHoje)

  if (idsDeEventosDeAtividadesRealizadas.length > 0) {
    eventosPlanejadosParaHoje = eventosPlanejadosParaHoje.filter(
      (evento) => !arrayDeIdsDeEventosDeAtividadesPlanejadas.includes(evento.id)
    )
  }

  const eventosNoFormatoDeAtividade: EventoASerIniciado[] =
    eventosPlanejadosParaHoje.map(mapearEventosNoFormatoDeAtividades)

  const atividadesEmAndamento = atividadesParaHojeFiltradas.map(
    mapearAtividadesParaModeloEventoCalendarioEmAndamento
  )

  const atividadesMapeadas = eventosNoFormatoDeAtividade.map(
    mapearAtividadesParaModeloEventoCalendarioPodeSerIniciada
  )

  return [...atividadesEmAndamento, ...atividadesMapeadas]
}

/**
 * Com base no array de atividadesQuePodemSerIniciadas filtra o array de eventosDaRequisicao
 * para retornar todos os outros eventos que ainda não podem ser iniciados
 * retornando os mesmos mapeados como ModeloEventoCalendario[]
 * @param eventosDaRequisicao
 * @param atividadesQuePodemSerIniciadas
 * @returns
 */
export const filtrarEMapearEventosQueAindaNaoPodemSerIniciados = (
  eventosDaRequisicao: AtividadePlanejada[]
): ModeloEventoCalendario[] => {
  const eventosQueNaoIniciamHoje = eventosDaRequisicao.filter(
    filtrarEventosQueNaoIniciamHoje
  )

  return eventosQueNaoIniciamHoje.map(
    usarEventosPlanejadosStore().mapearDTOsparaModelo
  )
}

/**
 * Com base no array de atividadesQuePodemSerIniciadas filtra o array de atividadesDaRequisicao
 * para retornar todas as outras atividades que ainda não podem ser iniciadas
 * retornando os mesmos mapeados como ModeloEventoCalendario[]
 * @param atividadesDaRequisicao
 * @param atividadesQuePodemSerIniciadas
 * @returns
 */
export const filtrarEMapearAtividadesQueAindaNaoPodemSerIniciadas = (
  atividadesDaRequisicao: Atividade[],
  atividadesQuePodemSerIniciadas: ModeloEventoCalendarioPodeSerIniciada[]
): ModeloEventoCalendario[] => {
  const atividadesQueNaoPodemSerIniciadas = atividadesDaRequisicao.filter(
    (atividade) =>
      !atividadesQuePodemSerIniciadas.some(
        (atividadeQuePodeSerIniciada) =>
          atividadeQuePodeSerIniciada.id === atividade.id
      ) && !atividade.deletado
  )
  
  return atividadesQueNaoPodemSerIniciadas.map(
    mapearAtividadesParaModeloEventoCalendario
  )
}

/**
 * Continuação da correção paliativa #1052
 * Caso o usuário tenha filtrado as atividades por ocorrências
 * antes de retornar o array de atividades é removido as atividades que possuem problemas
 * se não devolve o array de atividades sem alterações
 * @param atividades
 * @param possuiFiltroDeOcorrencias
 * @returns
 */
export const filtrarAtividadesSemOcorrenciaCasoTenhaOFiltro = (
  atividades:
    | ModeloEventoCalendario[]
    | ModeloEventoCalendarioPodeSerIniciada[],
  possuiFiltroDeOcorrencias: boolean
): ModeloEventoCalendario[] | ModeloEventoCalendarioPodeSerIniciada[] => {
  if (possuiFiltroDeOcorrencias) {
    return atividades.filter((atividade) => !atividade.problemas?.length)
  }

  return atividades
}
